import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/Toast';
import { Button, Input, Select, Card } from '../components/ui';

// OptionSet Tipo Area (deve corrispondere a Dataverse)
const TIPI_AREA = [
  { value: '100000000', label: 'Ala' },
  { value: '100000001', label: 'Piano' },
  { value: '100000002', label: 'Nucleo' },
  { value: '100000003', label: 'Stanza' }
];

// Gerarchia: indice piu basso = livello superiore
const GERARCHIA_TIPI = ['100000000', '100000001', '100000002', '100000003'];

function NuovoAreaPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { apiRequest } = useAuth();
  const { showToast } = useToast();

  // Parametri URL
  const editId = searchParams.get('id');
  const rsaIdParam = searchParams.get('rsaId');
  const parentIdParam = searchParams.get('parentId');
  const tipoAreaParam = searchParams.get('tipoArea');
  const isEditMode = !!editId;

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditMode);
  const [errors, setErrors] = useState({});
  const [rsaList, setRsaList] = useState([]);
  const [loadingRsa, setLoadingRsa] = useState(true);
  const [rsaNome, setRsaNome] = useState('');
  const [areeDisponibili, setAreeDisponibili] = useState([]);
  const [loadingAree, setLoadingAree] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    phc_name: '',
    rsaId: rsaIdParam || '',
    phc_tipoarea: tipoAreaParam || '',
    parentId: parentIdParam || '',
    phc_codice: '',
    phc_descrizione: '',
    phc_ordine: '',
    phc_capacita: '',
    phc_superficie: '',
    phc_attivo: true,
    phc_note: ''
  });

  // Carica lista RSA
  useEffect(() => {
    const loadRsaList = async () => {
      setLoadingRsa(true);
      try {
        const result = await apiRequest('/rsa');
        setRsaList((result.value || []).map(r => ({
          value: r.id,
          label: r.nome
        })));

        // Se c'e' rsaIdParam, trova il nome
        if (rsaIdParam) {
          const rsa = (result.value || []).find(r => r.id === rsaIdParam);
          if (rsa) setRsaNome(rsa.nome);
        }
      } catch (error) {
        console.error('Error loading RSA list:', error);
      } finally {
        setLoadingRsa(false);
      }
    };

    loadRsaList();
  }, [apiRequest, rsaIdParam]);

  // Carica aree disponibili per parent quando cambia RSA o tipo area
  useEffect(() => {
    const loadAree = async () => {
      if (!formData.rsaId) {
        setAreeDisponibili([]);
        return;
      }

      setLoadingAree(true);
      try {
        const result = await apiRequest(`/rsa/${formData.rsaId}/aree?soloAttive=true`);
        setAreeDisponibili(result.value || []);
      } catch (error) {
        console.error('Error loading aree:', error);
        setAreeDisponibili([]);
      } finally {
        setLoadingAree(false);
      }
    };

    loadAree();
  }, [formData.rsaId, apiRequest]);

  // Carica dati area in modalita' modifica
  useEffect(() => {
    if (!isEditMode) return;

    const loadArea = async () => {
      setLoadingData(true);
      try {
        const data = await apiRequest(`/aree/${editId}`);

        setFormData({
          phc_name: data.nome || '',
          rsaId: data.rsaId || '',
          phc_tipoarea: data.tipoArea !== undefined ? String(data.tipoArea) : '',
          parentId: data.parentId || '',
          phc_codice: data.codice || '',
          phc_descrizione: data.descrizione || '',
          phc_ordine: data.ordine !== undefined ? String(data.ordine) : '',
          phc_capacita: data.capacita !== undefined ? String(data.capacita) : '',
          phc_superficie: data.superficie !== undefined ? String(data.superficie) : '',
          phc_attivo: data.attivo !== false,
          phc_note: data.note || ''
        });

        if (data.rsaNome) setRsaNome(data.rsaNome);
      } catch (error) {
        console.error('Error loading area:', error);
        showToast('Errore nel caricamento dell\'area', 'error');
        navigate('/rsa');
      } finally {
        setLoadingData(false);
      }
    };

    loadArea();
  }, [editId, isEditMode, apiRequest, navigate, showToast]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }

    // Se cambia tipo area, resetta parent se non piu' valido
    if (name === 'phc_tipoarea' && formData.parentId) {
      const parentArea = areeDisponibili.find(a => a.id === formData.parentId);
      if (parentArea) {
        const parentIndex = GERARCHIA_TIPI.indexOf(String(parentArea.tipoArea));
        const newIndex = GERARCHIA_TIPI.indexOf(value);
        if (newIndex <= parentIndex) {
          setFormData(prev => ({ ...prev, parentId: '' }));
        }
      }
    }
  };

  // Filtra parent disponibili in base al tipo area selezionato
  const getParentOptions = () => {
    if (!formData.phc_tipoarea) return [];

    const tipoIndex = GERARCHIA_TIPI.indexOf(formData.phc_tipoarea);

    // Filtra solo aree di livello superiore (indice piu basso)
    return areeDisponibili
      .filter(a => {
        const areaIndex = GERARCHIA_TIPI.indexOf(String(a.tipoArea));
        return areaIndex < tipoIndex; // Solo livelli superiori
      })
      .map(a => ({
        value: a.id,
        label: `${a.tipoAreaLabel}: ${a.nome}`
      }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.phc_name.trim()) {
      newErrors.phc_name = 'Il nome e\' obbligatorio';
    }

    if (!formData.rsaId) {
      newErrors.rsaId = 'La RSA e\' obbligatoria';
    }

    if (!formData.phc_tipoarea) {
      newErrors.phc_tipoarea = 'Il tipo area e\' obbligatorio';
    }

    // Validazione numeri
    if (formData.phc_ordine && isNaN(parseInt(formData.phc_ordine))) {
      newErrors.phc_ordine = 'Deve essere un numero';
    }
    if (formData.phc_capacita && isNaN(parseInt(formData.phc_capacita))) {
      newErrors.phc_capacita = 'Deve essere un numero';
    }
    if (formData.phc_superficie && isNaN(parseFloat(formData.phc_superficie))) {
      newErrors.phc_superficie = 'Deve essere un numero';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showToast('Correggi gli errori nel form', 'error');
      return;
    }

    setLoading(true);

    try {
      const dataToSend = {
        phc_name: formData.phc_name,
        rsaId: formData.rsaId,
        phc_tipoarea: parseInt(formData.phc_tipoarea),
        parentId: formData.parentId || null,
        phc_codice: formData.phc_codice || null,
        phc_descrizione: formData.phc_descrizione || null,
        phc_ordine: formData.phc_ordine ? parseInt(formData.phc_ordine) : null,
        phc_capacita: formData.phc_capacita ? parseInt(formData.phc_capacita) : null,
        phc_superficie: formData.phc_superficie ? parseFloat(formData.phc_superficie) : null,
        phc_attivo: formData.phc_attivo,
        phc_note: formData.phc_note || null
      };

      // Rimuovi campi null
      Object.keys(dataToSend).forEach(key => {
        if (dataToSend[key] === null || dataToSend[key] === '') {
          delete dataToSend[key];
        }
      });

      if (isEditMode) {
        await apiRequest(`/aree/${editId}`, {
          method: 'PUT',
          body: JSON.stringify(dataToSend)
        });
        showToast('Area aggiornata con successo!', 'success');
      } else {
        await apiRequest('/aree', {
          method: 'POST',
          body: JSON.stringify(dataToSend)
        });
        showToast('Area creata con successo!', 'success');
      }

      // Torna alla RSA
      if (formData.rsaId) {
        navigate(`/rsa/${formData.rsaId}?tab=aree`);
      } else {
        navigate('/rsa');
      }
    } catch (error) {
      console.error('Error saving area:', error);
      showToast(error.message || 'Errore durante il salvataggio', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  const parentOptions = getParentOptions();
  const tipoAreaLabel = TIPI_AREA.find(t => t.value === formData.phc_tipoarea)?.label;

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <a href="/rsa" className="hover:text-primary-600">RSA</a>
          {rsaNome && (
            <>
              <span>/</span>
              <a href={`/rsa/${formData.rsaId || rsaIdParam}`} className="hover:text-primary-600">{rsaNome}</a>
            </>
          )}
          <span>/</span>
          <span>Aree</span>
          <span>/</span>
          <span>{isEditMode ? 'Modifica' : 'Nuova'}</span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? 'Modifica Area' : 'Nuova Area'}
        </h1>
        <p className="text-gray-500 mt-1">
          {isEditMode
            ? 'Modifica i dati dell\'area'
            : 'Definisci una nuova area della struttura (Ala, Piano, Nucleo o Stanza)'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Sezione: Identificazione */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>🗺️</span> Identificazione Area
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Select
                label="RSA di appartenenza *"
                name="rsaId"
                value={formData.rsaId}
                onChange={handleChange}
                options={rsaList}
                placeholder={loadingRsa ? 'Caricamento...' : 'Seleziona RSA...'}
                error={errors.rsaId}
                disabled={!!rsaIdParam || isEditMode}
              />
            </div>

            <Select
              label="Tipo Area *"
              name="phc_tipoarea"
              value={formData.phc_tipoarea}
              onChange={handleChange}
              options={TIPI_AREA}
              placeholder="Seleziona tipo..."
              error={errors.phc_tipoarea}
              disabled={isEditMode} // Non modificare tipo in edit
            />

            <Input
              label="Nome *"
              name="phc_name"
              value={formData.phc_name}
              onChange={handleChange}
              placeholder={
                formData.phc_tipoarea === '100000000' ? 'es. Ala Nord, Ala Est' :
                formData.phc_tipoarea === '100000001' ? 'es. Piano Terra, Piano 1' :
                formData.phc_tipoarea === '100000002' ? 'es. Nucleo Azzurro, Nucleo A' :
                formData.phc_tipoarea === '100000003' ? 'es. Stanza 101, Bagno 3' :
                'Nome dell\'area'
              }
              error={errors.phc_name}
              maxLength={200}
            />

            <Input
              label="Codice (opzionale)"
              name="phc_codice"
              value={formData.phc_codice}
              onChange={handleChange}
              placeholder="es. A1, P02, NC-B"
              helperText="Codice identificativo breve"
              maxLength={50}
            />

            <Input
              label="Ordine visualizzazione"
              name="phc_ordine"
              type="number"
              value={formData.phc_ordine}
              onChange={handleChange}
              placeholder="es. 1, 2, 3"
              helperText="Per ordinare le aree"
              error={errors.phc_ordine}
            />
          </div>
        </Card>

        {/* Sezione: Gerarchia */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>🔗</span> Posizione nella Gerarchia
          </h2>

          {!formData.phc_tipoarea ? (
            <div className="text-center py-6 text-gray-500">
              <p>Seleziona prima il tipo di area per definire la gerarchia</p>
            </div>
          ) : formData.phc_tipoarea === '100000000' ? (
            <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-800">
              <p className="font-medium">Ala = Livello radice</p>
              <p className="text-blue-700 mt-1">
                Le ali non hanno un parent - sono al livello piu' alto della gerarchia.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm">
                <p className="font-medium text-gray-700">Gerarchia: Ala &gt; Piano &gt; Nucleo &gt; Stanza</p>
                <p className="text-gray-500 mt-1">
                  {tipoAreaLabel && `Un "${tipoAreaLabel}" puo' appartenere a: `}
                  {formData.phc_tipoarea === '100000001' && 'Ala (o nessun parent)'}
                  {formData.phc_tipoarea === '100000002' && 'Ala, Piano (o nessun parent)'}
                  {formData.phc_tipoarea === '100000003' && 'Ala, Piano, Nucleo (o nessun parent)'}
                </p>
              </div>

              <Select
                label="Area Parent (opzionale)"
                name="parentId"
                value={formData.parentId}
                onChange={handleChange}
                options={[
                  { value: '', label: '-- Nessun parent (radice) --' },
                  ...parentOptions
                ]}
                placeholder={loadingAree ? 'Caricamento...' : 'Seleziona area parent...'}
                disabled={loadingAree || parentOptions.length === 0}
                helperText={
                  parentOptions.length === 0 && formData.rsaId
                    ? `Nessuna area di livello superiore disponibile per questa RSA`
                    : 'Lascia vuoto se questa area e\' al livello radice'
                }
              />
            </>
          )}
        </Card>

        {/* Sezione: Dettagli */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>📋</span> Dettagli
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Capacita'"
              name="phc_capacita"
              type="number"
              value={formData.phc_capacita}
              onChange={handleChange}
              placeholder="es. 10"
              helperText={
                formData.phc_tipoarea === '100000003' ? 'Posti letto nella stanza' :
                formData.phc_tipoarea === '100000002' ? 'Numero stanze nel nucleo' :
                'Capacita\' complessiva'
              }
              error={errors.phc_capacita}
            />

            <Input
              label="Superficie (mq)"
              name="phc_superficie"
              type="number"
              step="0.01"
              value={formData.phc_superficie}
              onChange={handleChange}
              placeholder="es. 150.5"
              error={errors.phc_superficie}
            />

            <div className="md:col-span-2">
              <Input
                label="Descrizione"
                name="phc_descrizione"
                value={formData.phc_descrizione}
                onChange={handleChange}
                placeholder="Descrizione dell'area..."
                maxLength={2000}
              />
            </div>

            <div className="md:col-span-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="phc_attivo"
                  checked={formData.phc_attivo}
                  onChange={handleChange}
                  className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Area attiva
                </span>
                <span className="text-sm text-gray-500">
                  (deseleziona per aree temporaneamente non utilizzate)
                </span>
              </label>
            </div>
          </div>
        </Card>

        {/* Sezione: Note */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>📝</span> Note
          </h2>

          <textarea
            name="phc_note"
            value={formData.phc_note}
            onChange={handleChange}
            placeholder="Note aggiuntive sull'area..."
            className="form-input w-full h-24"
            maxLength={4000}
          />
        </Card>

        {/* Form Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              if (formData.rsaId || rsaIdParam) {
                navigate(`/rsa/${formData.rsaId || rsaIdParam}?tab=aree`);
              } else {
                navigate('/rsa');
              }
            }}
          >
            Annulla
          </Button>

          <Button
            type="submit"
            variant="primary"
            disabled={loading}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {isEditMode ? 'Salvataggio...' : 'Creazione...'}
              </span>
            ) : (
              isEditMode ? 'Salva Modifiche' : 'Crea Area'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default NuovoAreaPage;
